<script lang="ts">
	import ActiveFilters from '$lib/components/category/filters/ActiveFilters.svelte'
	import PriceFilter from '$lib/components/category/filters/PriceFilter.svelte'
	import BrandsFilter from '$lib/components/category/filters/BrandsFilter.svelte'
	import SubcatFilter from './filters/SubcatFilter.svelte'
	import FilterOpener from './FilterOpener.svelte'
	import Checkbox from '@smui/checkbox'
	import { getIntSearchParam, is_set_array, is_set_object } from '$lib/js/helpers'
	import { type ProductsEndpoint, setDefaultCurrentPage, updateCurrentPage } from '$lib/js/products'
	import { storeActiveFilters, storeCurrentPage } from '$lib/js/stores'
	import { beforeNavigate, goto } from '$app/navigation'
	import { page } from '$app/state'
	import { t } from 'svelte-i18n-lingui'
	import type { BaseProduct, FullProduct, ProductQueryOptions, CategoryChild } from '$lib/js/elasticsearch/types'
	import { getContextScope } from '$lib/js/helpers/i18n'

	interface Props {
		options: ProductQueryOptions;
		products?: (BaseProduct | FullProduct)[];
		type?: string;
		endpoint: ProductsEndpoint;
	}

	let {
		options = $bindable(),
		products = $bindable([]),
		type = '',
		endpoint
	}: Props = $props();

	const scope = getContextScope()

	/* Get subcategories for filtering */
	let restartBrands = $state(false);
	let restartCats = $state(false);
	
	/* Mobile filters */
	let uClass = $state('')

	function toggleFilters() {
		uClass = uClass === 'open' ? 'closed' : 'open'
	}

	let activePromo = $derived(page.url.searchParams.get('promo') === 'true')
	let filteredSubcats = $derived(page.data.subcats ? page.data.subcats.filter((cat: CategoryChild) => cat.product_count !== 0) : [])

	function togglePromo() {
		activePromo === false ? goto(page.url.pathname + '?p=1&promo=true') : goto(page.url.pathname)
	}

	$effect(() => {
		const newPage = getIntSearchParam(page.url, 'p', 1)
		if ($storeCurrentPage !== newPage) {
			$storeCurrentPage = newPage
		}
	});

  $effect(() => {
		if (!$storeCurrentPage && type !== 'search') {
	    options = setDefaultCurrentPage(options)
	  } else if (type !== 'search') {
	    options = updateCurrentPage(options)
	  }
	});

	beforeNavigate(() => {
		$storeActiveFilters = {}
	})
</script>

<div class="category-filters {type}" class:selected-filters={is_set_object($storeActiveFilters)}>
	<button class="open" onclick={() => toggleFilters()}>
		{#if is_set_object($storeActiveFilters)}
			<span class="num-filters">{Object.keys($storeActiveFilters).length}</span>
		{/if}
		<h3 class="lead-1">{$t`Filters`}</h3>
	</button>

	<div class="category-filters__wrapper {uClass}">
		<button class="category-filters__overlay" onclick={() => toggleFilters()}></button>
		<div class="category-filters__inner">
			<button onclick={() => toggleFilters()} class="close">
				<h3>{$t`Filters`}</h3>
				<span class="icon icon-cross"></span>
			</button>

			<ActiveFilters bind:products bind:options bind:restartBrands bind:restartCats {type} {endpoint} />

			<FilterOpener title={$t`Price`} type="price">
				<div class="category-filters__price">
					<PriceFilter bind:products bind:options {type} {endpoint} />
				</div>
			</FilterOpener>

			{#if page.data.promo_prods && page.data.promo_prods.length >= 1}
				<button class="category-filters__promo" onclick={() => togglePromo()}>
					<Checkbox bind:checked={activePromo} />
					<div class="label">{$t`View promotions`}</div>
				</button>
			{/if}

			{#if page.route.id === '/[lang]/c/[...cat_url]' || page.route.id === '/[lang]/catalogsearch/result'
			 || type === 'search' || page.route.id === '/[lang]/t/[tag_url]'}
				{#key restartBrands}
					<FilterOpener title={$t`Our brands`}>
						<BrandsFilter bind:products bind:options {type} {endpoint} />
					</FilterOpener>
				{/key}
			{/if}

			{#if page.route.id === '/[lang]/brands/[brand_slug]' || page.route.id === '/[lang]/catalogsearch/result' || page.route.id === '/[lang]/c/[...cat_url]/b/[brand_url]' || type === 'search'}
				{#key restartCats}
					<FilterOpener title={$t`Subcategories`}>
						<SubcatFilter bind:products bind:options {type} {endpoint} />
					</FilterOpener>
				{/key}
			{:else if is_set_array(page.data.subcats)}
				<FilterOpener title={$t`Subcategories`}>
					<div class="subcategory-filters">
						<div class="subcategory-filters__cats">
							{#each filteredSubcats as subcat}
								<a class="subcat link" href="/{scope}/{subcat.url_path}">{subcat.name}</a>
							{/each}
						</div>
					</div>
				</FilterOpener>
			{/if}
		</div>
	</div>
</div>

<style lang="scss">
	.loading {
		margin-top: 30px;
	}

	.category-filters {
		&.selected-filters {
			max-height: calc(100vh - 180px);
			overflow-y: auto;
			overflow-x: hidden;
			scrollbar-width: none;
		}
		@media (max-width: 768px) {
			flex-grow: 1;
		}

		@media (min-width: 769px) {
			width: 240px;
		}

		h3 {
			@media (min-width: 769px) {
				margin: 0;
			}
		}

		button.open {
			position: relative;

			@media (min-width: 1025px) {
				display: none;
			}

			@media (max-width: 1024px) {
				background: white;
				padding: 4px 22px;
				border: 1px solid black;

				h3 {
					display: flex;
					align-items: center;
					margin: 0;
					text-align: left;
				}
			}

			@media (max-width: 1024px) {
				background: #fff;
				border: none;
				box-shadow: 0px 0px 20px 4px rgba(0, 0, 0, 0.12);

				margin: 12px 12px 12px 20px;
				border-radius: 6px;
				padding: 6px 15px;
				width: calc(100% - 40px);

				h3 {
					height: 24px;
				}
			}

			span.num-filters {
				position: absolute;
				display: flex;
				justify-content: center;
				align-items: center;
				color: white;
				top: 4px;
				right: -14px;
				width: 28px;
				height: 28px;
				background: var(--secondary);
				font-weight: 700;
				border-radius: 28px;

				@media (min-width: 1025px) {
					display: none;
				}
			}
		}

		&__overlay {
			@media (min-width: 1025px) {
				display: none;
			}
		}

		&__wrapper {
			transition: all linear 0.25s;

			@media (max-width: 1024px) {
				visibility: hidden;
				opacity: 0;
				position: fixed;
				top: 0;
				right: -100%;
				bottom: 0;
				z-index: 9;
				width: 100%;
				display: flex;
				justify-content: flex-end;
			}

			.close {
				display: flex;
				width: 100%;
				justify-content: space-between;
				margin-bottom: 20px;

				@media (min-width: 1025px) {
					display: none;
				}

				span {
					font-size: 20px;
				}
			}

			&.open {
				@media (max-width: 1024px) {
					right: 0;
					width: 100%;
					opacity: 1;
					visibility: visible;
				}

				.category-filters__overlay {
					@media (max-width: 1024px) {
						background-color: rgba(0, 0, 0, 0.3);
						opacity: 1;
					}
				}
			}
		}

		&__inner {
			@media (max-width: 1024px) {
				width: 280px;
				min-width: 240px;
				background: white;
				padding: 18px;
				box-shadow: 0px 4px 20px 10px rgba(0, 0, 0, 0.12);
				overflow-y: scroll;
			}
		}

		&__overlay {
			width: 100%;
			opacity: 0;
		}

		&__promo {
			display: flex;
			align-items: center;
			margin-bottom: 25px;
			margin-left: -10px;

			.label {
				font-weight: 700;
			}
		}

		&.search {
			@media (max-width: 1024px) {
				width: 140px;

				button.open {
					width: calc(100% - 30px);
					margin: 15px;
					background-color: white;
					border-radius: 40px;

					.num-filters {
						right: 5px;
					}
				}
			}
		}
	}

	.subcategory-filters {
		h3 {
			margin-bottom: 15px;
		}

		.active-subcat {
			color: green;
			order: 2;
			margin-top: 15px;

			span {
				margin-right: 8px;
			}
		}

		&__cats {
			display: flex;
			flex-direction: column;
			align-items: flex-start;
			background-color: var(--grey);
			border-radius: 5px;
			max-height: 130px;
			padding: 7px 10px;
			overflow-y: auto;
		}

		.subcat {
			display: block;
			padding: 2px 0;
			color: black;
			order: 1;
			text-align: left;
			line-height: 1.2;

			&:hover {
				color: var(--main);
			}

			&.link {
				padding: 4px 0;
			}
		}

		.active {
			color: var(--main);
			font-weight: 700;
		}
	}
</style>
